import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as Localization from 'expo-localization';
import AsyncStorage from '@react-native-async-storage/async-storage';
import i18n from '../i18n';

// Query keys
export const languageKeys = {
  all: ['language'] as const,
  current: () => [...languageKeys.all, 'current'] as const,
};

// Helper function to safely set i18n locale
const setSafeI18nLocale = (locale: string | undefined | null) => {
  const safeLocale =
    locale && typeof locale === 'string' && locale.trim() ? locale : 'en';
  try {
    i18n.locale = safeLocale;
  } catch (error) {
    console.warn('Failed to set i18n locale, using default:', error);
    i18n.locale = 'en';
  }
};

// Hook to get current language
const useCurrentLanguage = () => {
  return useQuery({
    queryKey: languageKeys.current(),
    queryFn: async () => {
      try {
        const savedLanguage = await AsyncStorage.getItem('userLanguage');
        if (savedLanguage) {
          setSafeI18nLocale(savedLanguage);
          return savedLanguage;
        }

        // If no saved language, use device language or default to English
        let deviceLanguage: string = 'en';
        try {
          if (Localization.getLocales) {
            const locales = Localization.getLocales();
            if (
              locales &&
              locales.length > 0 &&
              typeof locales[0].languageCode === 'string'
            ) {
              deviceLanguage = locales[0].languageCode || 'en';
            }
          }
        } catch (error) {
          console.warn('Failed to get device locale, using default:', error);
        }

        // Check if we support the device language
        const supportedLanguages = ['en', 'es', 'fr', 'de', 'it', 'ja'];
        const langToUse = supportedLanguages.includes(deviceLanguage)
          ? deviceLanguage
          : 'en';
        setSafeI18nLocale(langToUse);
        await AsyncStorage.setItem('userLanguage', langToUse);
        return langToUse;
      } catch (error) {
        console.error('Failed to load language preference:', error);
        return 'en';
      }
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

// Main language hook
export const useLanguage = () => {
  const queryClient = useQueryClient();
  const { data: currentLanguage = 'en' } = useCurrentLanguage();

  // Change language mutation
  const changeLanguageMutation = useMutation({
    mutationFn: async (langCode: string) => {
      try {
        setSafeI18nLocale(langCode);
        await AsyncStorage.setItem('userLanguage', langCode);
        return langCode;
      } catch (error) {
        console.error('Failed to save language preference:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(languageKeys.current(), data);
      // Force re-render by invalidating related queries
      queryClient.invalidateQueries({ queryKey: languageKeys.all });
    },
  });

  return {
    currentLanguage,
    changeLanguage: changeLanguageMutation.mutateAsync,
    forceRender: currentLanguage, // Use currentLanguage as a render trigger
  };
};
